import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Sparkles, Wand2 } from "lucide-react";

import { extractFromText } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VoiceDictationButton } from "@/components/VoiceDictationButton";
import { useExtractionStore, type ExtractedField } from "./useExtractionStore";

/**
 * "Dopuni opis" — on the Provjeri screen the architect dictates (or types)
 * extra description straight into the form while reviewing it, and the
 * fields fill from it. Reuses the same
 * NL extractor as the description path, which already enforces exactly the
 * intended policy:
 *   - fills EMPTY fields,
 *   - refines soft (NL/form) values,
 *   - never overwrites a measured DXF/IFC value (passed as ``already_filled``
 *     so the model returns null for those),
 *   - caps dictated numbers at "provjeri" confidence (gold stamp).
 *
 * The parent wires ``onExtracted`` to the live form's ``applyNlExtraction``
 * so fields update in place.
 */
export function DictateToFillPanel({
  onExtracted,
}: {
  onExtracted: (
    extracted: Record<string, ExtractedField>,
    warnings: string[],
  ) => void;
}) {
  const [text, setText] = useState("");
  const lastExtracted = useRef<string>("");

  const extract = useMutation({
    mutationFn: async () => {
      const current = useExtractionStore.getState().confidence;
      return (await extractFromText(
        text,
        Object.keys(current).length ? current : undefined,
      )) as {
        extracted?: Record<string, ExtractedField>;
        warnings?: string[];
      };
    },
    onSuccess: (raw) => {
      onExtracted(raw.extracted ?? {}, raw.warnings ?? []);
    },
  });

  // Auto-apply once the dictation/typing settles — one call per pause,
  // guarded so unchanged text never re-fires. Manual button forces a re-run.
  useEffect(() => {
    const trimmed = text.trim();
    if (trimmed.length < 8) return;
    if (trimmed === lastExtracted.current) return;
    if (extract.isPending) return;
    const timer = window.setTimeout(() => {
      lastExtracted.current = trimmed;
      extract.mutate();
    }, 1600);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, extract.isPending]);

  const hasText = text.trim().length > 0;

  return (
    <div className="mb-4 rounded-xl border border-ind-100 bg-ind-50/50 p-4">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-ind-100 text-ind-700">
          <Wand2 className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h4 className="text-[0.92rem] font-bold text-stone-800">
              Dopuni opis
            </h4>
            <VoiceDictationButton
              disabled={extract.isPending}
              onTranscript={(t) =>
                setText((d) => (d.trim() ? `${d.trim()} ${t}` : t))
              }
            />
          </div>
          <p className="mt-0.5 text-[0.78rem] leading-snug text-stone-500">
            Reci ili dopiši detalje — popunit ću prazna polja iz toga.
            Izmjereno iz nacrta ostaje netaknuto.
          </p>
        </div>
      </div>

      <Textarea
        rows={3}
        value={text}
        disabled={extract.isPending}
        onChange={(e) => setText(e.target.value)}
        className="mt-3 w-full min-h-[84px] bg-white"
        placeholder={"npr. „OIB investitora 12345678901, parkirnih mjesta 3, broj projekta ABC-123”"}
      />

      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[0.78rem] text-stone-500">
          {extract.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin text-ind-600" />
              Primjenjujem na formu…
            </>
          ) : extract.isSuccess ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              Primijenjeno — provjeri žuto označena polja.
            </>
          ) : (
            "Primjenjuje se automatski dok govoriš ili pišeš."
          )}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasText || extract.isPending}
          onClick={() => {
            lastExtracted.current = "";
            extract.mutate();
          }}
        >
          {extract.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          Primijeni
        </Button>
      </div>

      {extract.error instanceof Error && (
        <p className="mt-2 text-[0.8rem] text-red-700">
          Greška: {extract.error.message}
        </p>
      )}
    </div>
  );
}
